import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Button } from '@zolteam/zol-admin-ui';
// Services
import { postCreateUser } from '../../services/parazol/parazolService';
import { useGetAdminRolesListQuery } from '../../services/parazol/parazolQueries';
// Context
import { useAppContext } from '../../store/AppContext';
// Locales
import fr from '../../locales/fr.json';

const FORM_NAME = 'newUser';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Utilisateur',
  properties: {
    firstName: {
      type: 'string',
      description: 'Prénom',
      x_layout: '1/1/1/7',
    },
    lastName: {
      type: 'string',
      description: 'Nom',
      x_layout: '1/7/1/13',
    },
    emailAddress: {
      type: 'string',
      format: 'email',
      description: 'Email',
      x_layout: '2/1/2/13',
    },
    roleIds: {
      type: 'array',
      description: 'Rôles',
      x_layout: '3/1/3/13',
      items: {
        type: 'string',
        enum: [
          'ADMIN',
        ],
      },
    },
  },
  required: ['firstName', 'lastName', 'emailAddress', 'roleIds'],
  additionalProperties: false,
};

/**
 * UserForm component.
 * @returns {JSX.Element}
 */
function UserForm() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, dispatch] = useAppContext();

  // API Calls
  const { data: adminRolesData } = useGetAdminRolesListQuery({ pageNumber: 1, pageSize: 999 });
  const postCreateUserMutation = useMutation(postCreateUser, {
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('parazol.userNew.success'), type: 'success' },
      });
      navigate('/users');
    },
  });

  // Handlers
  /**
   * Handles the form submission to create a new user.
   * @param {object} formValues - The values from the form.
   * @param {string} formValues.emailAddress - The email address of the new user.
   * @param {string[]} formValues.roleIds - The list of role names for the new user.
   * @param {string} formValues.firstName - The first name of the new user.
   * @param {string} formValues.lastName - The last name of the new user.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    /**
     * @typedef {object} NewUserBody
     * @property {string} emailAddress
     * @property {string|null} password
     * @property {string|null} emailAddressConfirmationRedirectUrl
     * @property {string[]} roleIds
     * @property {string} firstName
     * @property {string} lastName
     */
    /** @type {NewUserBody} */
    const body = {
      emailAddress: formValues.emailAddress,
      password: null,
      emailAddressConfirmationRedirectUrl: `${import.meta.env.VITE_APP_URL}/confirm-password`,
      roleIds: formValues.roleIds
        .map((/** @type {string} */role) => adminRolesData.data
          .find((roleData) => roleData.name === role).id),
      firstName: formValues.firstName,
      lastName: formValues.lastName,
    };
    postCreateUserMutation.mutate(body);
  };

  return (
    <div className="padding-l">
      <h1 className="h1-default margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {t('parazol.userNew.title')}
      </h1>
      <Form
        name={FORM_NAME}
        className=""
        callbacks={{
          onSubmit,
        }}
        schema={SCHEMA}
        translations={{
          formValidation: fr.cortizol.formValidation,
        }}
      >
        {(formConfig, formInfo) => (
          <FormElements
            formConfig={formConfig}
            formTitle={formInfo.formName}
            translations={{
              selectOptionLabels: {
                ADMIN: t('parazol.userNew.roles.admin'),
                USER: t('parazol.userNew.roles.user'),
                BLOG: t('parazol.userNew.roles.blog'),
              },
            }}
          />
        )}
      </Form>

      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={postCreateUserMutation.isLoading}
      >
        {t('parazol.userNew.submit')}
      </Button>
    </div>
  );
}

export default UserForm;
